/*
 * Copyright (c) 2024. Dave van Rijn Development
 */
import './style/pdfTemplateCreator.scss';

import React, {Fragment, PureComponent, ReactElement} from 'react';
import {
    AwesomeIcon,
    DefaultPDFElementParams,
    directTimeout,
    dispatchCustomEvent,
    DvrdButton,
    generateUUID,
    IndexedObject,
    PDFElementParams,
    PDFElementPersist,
    PDFElementType,
    PdfFont,
    PDFOptions,
    PDFSubmitHandler,
    PDFTextVariables,
    ptToPx,
    setElementSettings,
    voidFunction
} from "../../../";
import PdfText from "./text/pdfText";
import classNames from 'classnames';
import {PdfElement} from "./element/pdfElement";
import PdfElementSettings from "./settings/pdfElementSettings";
import PdfImage from "./image/pdfImage";
import PdfInvoiceTable from "./invoiceTable/pdfInvoiceTable";
import {addToHistory, canRedo, canUndo, clearHistory, redo, undo} from "./history/pdfHistory";
import {IconName} from '@fortawesome/fontawesome-svg-core';

interface Props {
    onClickProceed?: PDFSubmitHandler;
    onPreview?: PDFSubmitHandler;
    onSubmit: PDFSubmitHandler;
    onChangeFont: (font: PdfFont, callback?: VoidFunction) => void;
    values: IndexedObject<string>;
    defaultItems?: DefaultPDFElementParams<any, any>[];
    resetItems: DefaultPDFElementParams<any, any>[];
    customVariables?: PDFTextVariables;
    id?: string;
    mainFont: PdfFont;
    disableElements: ('text' | 'image' | 'invoiceTable')[];
    maxWidth?: string;
    supportMultiPage: boolean;
    options?: PDFOptions;
    singleImage: boolean;
    headerButtons?: Array<ReactElement>;
    tableHeaderLabels?: [string, string, string, string, string];
}

interface State {
    elements: IndexedObject<{ element: React.ReactElement, config: DefaultPDFElementParams<any, any> }>;
    focusedElement: PdfElement | null;
    canUndo: boolean;
    canRedo: boolean;
}

export default class PDFTemplateCreator extends PureComponent<Props, State> {
    static defaultProps = {
        values: {},
        disableElements: [],
        supportMultiPage: false,
        singleImage: false,
    };
    private refObjects: IndexedObject<PdfElement>;

    constructor(props: Props) {
        super(props);
        this.refObjects = {};
        const elements: IndexedObject<{
            element: React.ReactElement,
            config: DefaultPDFElementParams<any, any>
        }> = this.processItems(props.defaultItems);
        this.state = {elements, focusedElement: null, canRedo: false, canUndo: false};
    }

    reloadTemplate(items: DefaultPDFElementParams<any, any>[]) {
        const elements: IndexedObject<{
            element: React.ReactElement,
            config: DefaultPDFElementParams<any, any>
        }> = this.processItems(items);
        this.setState({elements, canRedo: false, canUndo: false, focusedElement: null});
    }

    processItems = (items?: DefaultPDFElementParams<any, any>[],
                    convertUnits: boolean = true): IndexedObject<{
        element: React.ReactElement,
        config: DefaultPDFElementParams<any, any>
    }> => {
        const elements: IndexedObject<{ element: React.ReactElement, config: DefaultPDFElementParams<any, any> }> = {};
        if (items?.length) {
            items = JSON.parse(JSON.stringify(items));
            if (items) {
                this.refObjects = {};
                for (const item of items) {
                    setElementSettings(item, convertUnits);
                    const id = item.key || generateUUID();
                    elements[id] = this.getElement(id, item, convertUnits)
                }
            }
        }
        return elements;
    };

    onUndo = () => {
        if (canUndo()) {
            const {items, mainFont} = undo();
            if (items.length)
                this.props.onChangeFont(mainFont, () => {
                    this.setState(
                        {elements: this.processItems(items, false), canRedo: canRedo(), canUndo: canUndo()});
                });
        }
    };

    onRedo = () => {
        if (canRedo()) {
            const {items, mainFont} = redo();
            if (items.length)
                this.props.onChangeFont(mainFont, () => {
                    this.setState(
                        {elements: this.processItems(items, false), canRedo: canRedo(), canUndo: canUndo()});
                });
        }
    };

    onReset = () => {
        const {resetItems} = this.props;
        clearHistory();
        this.setState({elements: this.processItems(resetItems), canRedo: false, canUndo: false}, this.onSaveHistory);
    };

    onSaveHistory = () => {
        const {mainFont} = this.props, items: PDFElementParams<any, any>[] = [] =
            Object.values(this.refObjects).filter((element: PdfElement | null) => element !== null)
                .map((element: PdfElement) => element.getParams(false));
        addToHistory(items, mainFont);
        this.setState({canUndo: canUndo(), canRedo: canRedo()});
    };

    onFocusElement = (element: PdfElement) => {
        this.onResetElement();
        this.setState({focusedElement: null}, () => {
            this.setState({focusedElement: element});
        });
    };

    onResetElement = () => {
        this.setState({focusedElement: null});
        const focusedElements = document.querySelectorAll('div.pdf-draggable.focused');
        for (const element of focusedElements)
            element.classList.remove('focused');
    };

    onDeleteElement = (id: string) => () => {
        const {elements} = this.state;
        delete this.refObjects[id];
        delete elements[id];
        this.setState({elements: Object.assign({}, elements), focusedElement: null});
    };

    onAddElement = (type: PDFElementType) => (evt: React.MouseEvent) => {
        evt.stopPropagation();
        const id = generateUUID();
        const scrollTop = (document.documentElement.scrollTop || document.body.scrollTop) + 50;
        const newElements = Object.assign({}, this.state.elements);

        newElements[id] = this.getElement(id,
            {dimensions: {width: 150, height: 100, left: 50, top: scrollTop}, type});
        this.setState({elements: newElements});
    };

    onSubmit = () => {
        this.props.onSubmit(this.createSubmitItems());
    };

    onClickProceed = () => {
        const {onClickProceed} = this.props;
        if (onClickProceed)
            onClickProceed(this.createSubmitItems());
    };

    onPreview = () => {
        const {onPreview} = this.props;
        if (onPreview) onPreview(this.createSubmitItems());
    }

    onChangeFont = (font: PdfFont) => {
        const currentFont = this.props.mainFont;
        this.props.onChangeFont(font, this.onSaveHistory);
        dispatchCustomEvent('pdfFontChanged', {previousFont: currentFont, font});
    };

    createSubmitItems = (): PDFElementParams<any, any>[] => {
        return Object.values(this.refObjects).filter((element: PdfElement | null) => element !== null)
            .map((element: PdfElement) => element.getParams());
    };

    getElement = (id: string, config: DefaultPDFElementParams<any, any>,
                  convertUnits: boolean = true): {
        element: React.ReactElement,
        config: DefaultPDFElementParams<any, any>
    } => {
        const {dimensions, linkedID, persistent} = config;
        const position: { x: number; y: number } = {
            x: convertUnits ? ptToPx(dimensions?.left || 0) : (dimensions?.left || 0),
            y: convertUnits ? ptToPx(dimensions?.top || 0) : (dimensions?.top || 0),
        };
        const width = dimensions?.width;
        const height = dimensions?.height;
        const {values, mainFont, tableHeaderLabels} = this.props;
        const key: string = generateUUID();
        const sharedProps = {
            defaultPosition: position,
            onDelete: this.onDeleteElement(id),
            id,
            draggable: true,
            onFocusElement: this.onFocusElement,
            onSaveHistory: this.onSaveHistory,
            ref: (ref: any) => {
                this.refObjects[id] = ref;
            },
            defaultSettings: {...config.dimensions, ...config.options},
            key,
        }
        let element = <div/>;
        switch (config.type) {
            case PDFElementType.TEXT:
                element = <PdfText {...sharedProps} values={values} persistent={persistent} mainFont={mainFont}
                                   linkedID={linkedID}/>;
                break;
            case PDFElementType.IMAGE:
                element = <PdfImage {...sharedProps} values={values} width={width} height={height}
                                    persistent={persistent}/>;
                break;
            case PDFElementType.INVOICE_TABLE:
                element = <PdfInvoiceTable {...sharedProps} width={width} height={height} mainFont={mainFont}
                                           persistent={PDFElementPersist.PERSISTENT}
                                           tableHeaderLabels={tableHeaderLabels}/>;
                break;
        }
        return {element, config};
    };

    canRenderImageButton = (): boolean => {
        if (this.props.disableElements.includes('image')) return false;
        if (!this.props.singleImage) return true;
        const {elements} = this.state;
        const elementIds = Object.keys(elements);
        if (elementIds.length) {
            for (const id of elementIds) {
                const config = elements[id].config;
                if (config.type === PDFElementType.IMAGE) return false;
            }
        }
        return true;
    }

    renderElementButton = (icon: IconName, label: string, type: PDFElementType) => (
        <div className='element-button' onClick={this.onAddElement(type)}>
            <AwesomeIcon name={icon} className='element-icon'/>
            <label className='element-label'>+ {label}</label>
        </div>
    );

    componentDidMount = () => {
        directTimeout(this.onSaveHistory);
    };

    componentWillUnmount = () => {
        clearHistory();
    };

    render = () => {
        const {elements, focusedElement, canUndo, canRedo,} = this.state,
            {
                customVariables, onPreview, mainFont, onClickProceed, maxWidth
            } = this.props;
        const disableElements = this.props.options?.disableElements ?? this.props.disableElements;
        const supportMultiPage = this.props.options?.supportMultiPage ?? this.props.supportMultiPage;
        const enableTableHeaderColor = this.props.options?.enableTableHeaderColor;
        return (
            <div className='pdf-creator' onClick={this.onResetElement} style={{maxWidth: maxWidth ?? '100%'}}>
                <div className='footer-container'>
                    <div className='left-buttons'>
                        {onPreview !== undefined ? <DvrdButton onClick={this.onPreview} label='Voorbeeld'/> : <div/>}
                        {this.props.headerButtons?.map((el: ReactElement, index: number) => React.cloneElement(el, {key: index}))}
                    </div>
                    <div>
                        <DvrdButton onClick={this.onSubmit} label='Opslaan'/>
                        {onClickProceed !== undefined &&
                            <DvrdButton onClick={this.onClickProceed} label='Naar volgende stap'
                                        className='proceed-btn'/>}
                    </div>
                </div>
                <div/>
                <div className='elements-container'>
                    {this.canRenderImageButton() &&
                        this.renderElementButton('image', 'Afbeelding', PDFElementType.IMAGE)}
                    {!disableElements.includes('text') &&
                        this.renderElementButton('font', 'Tekst', PDFElementType.TEXT)}
                    {!disableElements.includes('invoiceTable') &&
                        this.renderElementButton('list', 'Factuurregels', PDFElementType.INVOICE_TABLE)}
                    <div className='history'>
                        <div className={classNames('history-button', !canUndo && 'disabled')}
                             onClick={canUndo ? this.onUndo : voidFunction}
                             title={canUndo ? 'Ongedaan maken' : undefined}>
                            <AwesomeIcon name='undo' className='history-icon'/>
                        </div>
                        <div className={classNames('history-button', !canRedo && 'disabled')}
                             onClick={canRedo ? this.onRedo : voidFunction}
                             title={canRedo ? 'Opnieuw doen' : undefined}>
                            <AwesomeIcon name='redo' className='history-icon'/>
                        </div>
                    </div>
                    <div className='history-reset'>
                        <DvrdButton onClick={this.onReset} label='Reset'/>
                    </div>
                </div>
                <div className='pdf-paper' onClick={this.onResetElement}>
                    {Object.keys(elements).map((id: string) => (
                        <Fragment key={id}>
                            {elements[id].element}
                        </Fragment>
                    ))}
                </div>
                <div className='right-col'>
                    <PdfElementSettings element={focusedElement} onReset={this.onResetElement}
                                        customVariables={customVariables} font={mainFont}
                                        enableTableHeaderColor={enableTableHeaderColor}
                                        onChangeFont={this.onChangeFont} supportMultiPage={supportMultiPage}/>
                </div>
            </div>
        )
    }
}
